import ChevronLeft from '@mdi/svg/svg/chevron-left.svg?react';
import ChevronRight from '@mdi/svg/svg/chevron-right.svg?react';
import Pencil from '@mdi/svg/svg/pencil.svg?react';
import {
  Button,
  Checkbox,
  Flex,
  KeyActions,
  MDIIcon,
  MDIIconButton,
  TextField,
  useLatest,
} from '@solinkcorp/solink-components';
import classNames from 'classnames';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type IEvent } from 'clients/PlatformApi';
import CameraThumbnail from 'components/CameraThumbnail/CameraThumbnail';
import { FullEventSpotCheckAnswer } from 'components/EventsPage/Event/SidekickAnswers/SidekickAnswers';
import S3Image from 'components/S3Image/S3Image';
import { type UseEventSpotCheckStructuredOutput } from 'components/SpotCheckPanel/hooks/useEventSpotCheckStructuredOutput';
import { type UseSpotCheckForm } from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import { type UseSpotCheckPreview } from 'components/SpotCheckPanel/hooks/useSpotCheckPreview';
import { type UseFormattedSpotCheckStructuredOutput } from 'components/SpotCheckPanel/hooks/useSpotCheckStructuredOutput';
import CenteredLoadingSpinner from 'loadingIndicator/CenteredLoadingSpinner';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import LoadingCylonSvg from 'src/images/loading-cylon.svg';
import { LocationStore } from 'stores/LocationStore';
import { useSelector } from 'stores/store';
import isShallowEqual from 'utils/isShallowEqual';
import { getValidationErrorMessage } from 'utils/yupUtils';
import EventDetailsSnapshotOverlay from './EventDetailsSnapshotOverlay/EventDetailsSnapshotOverlay';
import SpotCheckForm from './SpotCheckForm';
import styles from './SpotCheckFormView.module.sass';

interface SpotCheckFormViewProps {
  spotCheck?: ScheduledActivityEntity;
  eventSpotCheck?: AiNotification;
  spotCheckForm: UseSpotCheckForm;
  spotCheckPreview: UseSpotCheckPreview;
  createSpotCheckPreview: () => void;
  spotCheckStructuredOutput: UseFormattedSpotCheckStructuredOutput;
  eventSpotCheckStructuredOutput: UseEventSpotCheckStructuredOutput;
  handleGoBack: () => void;
  excludedCameraIds: string[];
  setExcludedCameraIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedCameraIndex: number;
  setSelectedCameraIndex: React.Dispatch<React.SetStateAction<number>>;
  timezoneId: string;
  formMode: 'create' | 'edit';
  isExistingSpotCheck: boolean;
  enterCameraSelectMode: () => void;
  sentEventSpotCheckRequest: boolean;
  eventSpotCheckAnswer: string;
  eventImagePreviewUrls: string[];
  totalEventsInLast30Days: number | undefined;
  isEventsEstimateBelowLimit: boolean;
  eventsMonthlyQuotaLimit: number | undefined;
  reportEvent: IEvent | undefined;
}
export default function SpotCheckFormView(props: SpotCheckFormViewProps) {
  const {
    spotCheck,
    spotCheckForm,
    spotCheckPreview,
    createSpotCheckPreview,
    spotCheckStructuredOutput,
    eventSpotCheckStructuredOutput,
    handleGoBack,
    excludedCameraIds,
    setExcludedCameraIds,
    selectedCameraIndex,
    setSelectedCameraIndex,
    timezoneId,
    isExistingSpotCheck,
    formMode,
    enterCameraSelectMode,
    sentEventSpotCheckRequest,
    eventSpotCheckAnswer,
    eventImagePreviewUrls,
    totalEventsInLast30Days,
    isEventsEstimateBelowLimit,
    eventsMonthlyQuotaLimit,
    reportEvent,
  } = props;

  const locale = useLocale();

  const [editTitleMode, setEditTitleMode] = React.useState(false);

  const { selectedCameraIds, spotCheckMode } = useSelector(
    (state) => ({
      selectedCameraIds: state.filter.selectedCameraIds ?? [],
      spotCheckMode: state.filter.spotCheckMode,
    }),
    isShallowEqual,
  );

  const hasSelectedCameras = selectedCameraIds.length > 0;

  const latestCreateSpotCheckPreview = useLatest(createSpotCheckPreview);

  React.useEffect(() => {
    if (selectedCameraIds.length && !isExistingSpotCheck) {
      latestCreateSpotCheckPreview.current();
    }
  }, [selectedCameraIds, latestCreateSpotCheckPreview, isExistingSpotCheck]);

  const titleErrorText = getValidationErrorMessage(spotCheckForm.spotCheckValidationError, 'title');

  React.useEffect(() => {
    setEditTitleMode(!!titleErrorText);
  }, [titleErrorText]);

  const spotCheckEvent = spotCheckPreview?.data;

  const selectedCamera = spotCheckEvent?.cameras?.[selectedCameraIndex];

  const isPolling = spotCheckPreview.eventPollingState === 'POLLING';

  const areCameras = !!spotCheckEvent?.cameras.length;

  const isAtleastTwoCameras = (spotCheckEvent?.cameras?.length ?? 0) > 1;

  const previewAnswer = spotCheckEvent?.details?.scheduledActivity?.metadata?.aiChatAnswers;

  const goToNextCamera = () => setSelectedCameraIndex(selectedCameraIndex + 1);

  const goToPreviousCamera = () => setSelectedCameraIndex(selectedCameraIndex - 1);

  const toggleExcludedCamera = () => {
    if (!selectedCamera?.chId) {
      return;
    }
    const cameraId = selectedCamera.chId;

    if (excludedCameraIds.includes(cameraId)) {
      setExcludedCameraIds(excludedCameraIds.filter((id) => id !== cameraId));
    } else {
      setExcludedCameraIds([...excludedCameraIds, cameraId]);
    }
  };

  const isCurrentCameraSelected = excludedCameraIds.includes(selectedCamera?.chId ?? '');

  const previousCameraDisabled = selectedCameraIndex <= 0;
  const nextCameraDisabled = selectedCameraIndex >= (spotCheckEvent?.cameras.length ?? 0) - 1;
  const isLivePreview = spotCheckMode === 'live-preview';
  const isEventSpotCheck = spotCheckMode === 'event';

  const shortcuts = [
    {
      shortcut: 'left',
      action: goToPreviousCamera,
      shouldActionRun: () => !previousCameraDisabled,
    },
    {
      shortcut: 'right',
      action: goToNextCamera,
      shouldActionRun: () => !nextCameraDisabled,
    },
  ];

  const handleBlurTitle = () => {
    if (!titleErrorText) {
      setEditTitleMode(false);
    }
  };
  const [selectedCameraIndexEventSpotCheck, setSelectedCameraIndexEventSpotCheck] =
    React.useState<number>(0);
  const handleNextCameraEventSpotCheck = () => {
    if (
      eventImagePreviewUrls?.length &&
      selectedCameraIndexEventSpotCheck < eventImagePreviewUrls.length - 1
    ) {
      setSelectedCameraIndexEventSpotCheck(selectedCameraIndexEventSpotCheck + 1);
    }
  };
  const handlePreviousCameraEventSpotCheck = () => {
    if (eventImagePreviewUrls?.length && selectedCameraIndexEventSpotCheck > 0) {
      setSelectedCameraIndexEventSpotCheck(selectedCameraIndexEventSpotCheck - 1);
    }
  };
  const shortcutsEventSpotCheck = [
    {
      shortcut: 'left',
      action: handlePreviousCameraEventSpotCheck,
    },
    {
      shortcut: 'right',
      action: handleNextCameraEventSpotCheck,
    },
  ];

  React.useEffect(() => {
    setSelectedCameraIndexEventSpotCheck(0);
  }, [spotCheckForm.selectedEventReport, setSelectedCameraIndexEventSpotCheck]);

  const isAtLeastTwoReportCameras =
    eventImagePreviewUrls?.length && eventImagePreviewUrls.length >= 2;

  const location = reportEvent?.siteId && LocationStore.getLocation(reportEvent?.siteId);

  const isSnapshotAvailable =
    reportEvent &&
    location &&
    isEventSpotCheck &&
    spotCheckForm.selectedEventReport &&
    !!eventImagePreviewUrls?.length &&
    eventImagePreviewUrls[selectedCameraIndexEventSpotCheck];

  const parseEventSpotCheckAnswer = (answer: string) => {
    try {
      return JSON.parse(answer);
    } catch (_error) {
      return answer;
    }
  };

  return (
    <div className={styles.spotCheckFormView}>
      {spotCheckStructuredOutput.isLoading || eventSpotCheckStructuredOutput.isLoading ? (
        <CenteredLoadingSpinner />
      ) : (
        <>
          <div className={styles.spotCheckFormHeader}>
            <MDIIconButton
              title={locale.scheduledAIChat.goBack}
              icon={ChevronLeft}
              fill="var(--mine-shaft)"
              onClick={handleGoBack}
              className={styles.spotCheckGoBackIconButton}
            />
            <Flex
              width="auto"
              alignItems="center"
              gap="0.5rem"
              className={styles.spotCheckTitleWrapper}
            >
              {editTitleMode ? (
                <TextField
                  value={spotCheckForm.title}
                  onChange={({ value }: { value: string }) => spotCheckForm.setTitle(value)}
                  onBlur={handleBlurTitle}
                  autoFocus
                  className={styles.spotCheckTitleInput}
                  errorText={titleErrorText}
                />
              ) : (
                <button className={styles.spotCheckGoBackButton} onClick={handleGoBack}>
                  {spotCheckForm.title}
                </button>
              )}{' '}
              {!editTitleMode && (
                <MDIIconButton
                  icon={Pencil}
                  title="edit"
                  iconSize={20}
                  fill="var(--color-dark-gray-lightest)"
                  onClick={() => setEditTitleMode(true)}
                />
              )}
            </Flex>
          </div>
          <div className={styles.spotCheckFormViewContent}>
            <div className={styles.spotCheckFormViewPreview}>
              <div
                className={classNames(styles.imageWrapper, {
                  [styles.camerasSelected]: hasSelectedCameras,
                })}
              >
                <div className={styles.imageBackground}>
                  {selectedCamera && (
                    <S3Image
                      id={selectedCamera.chId ?? selectedCamera.id}
                      url={selectedCamera.thumbnail}
                      className={classNames(styles.cameraImage, {
                        [styles.excluded]: excludedCameraIds.includes(selectedCamera.chId ?? ''),
                      })}
                    />
                  )}
                  {isSnapshotAvailable && (
                    <>
                      <CameraThumbnail
                        cameraId={eventImagePreviewUrls[selectedCameraIndexEventSpotCheck]}
                        thumbnailType="snapshot"
                        thumbnail={eventImagePreviewUrls[selectedCameraIndexEventSpotCheck]}
                        spotCheckImage
                      />
                      <EventDetailsSnapshotOverlay
                        event={reportEvent}
                        location={location}
                        canExpand={true}
                      />
                    </>
                  )}
                </div>
                <div className={styles.imageOverlay}>
                  {!isEventSpotCheck && !hasSelectedCameras && (
                    <Button
                      text={locale.scheduledAIChat.selectCamerasSpotCheck}
                      onClick={enterCameraSelectMode}
                    />
                  )}
                  {isPolling && !areCameras && (
                    <Flex width="auto" gap="1rem" alignItems="center">
                      <span data-testid="spot-check-loading-preview">
                        {locale.scheduledAIChat.loadingPreview}
                      </span>{' '}
                      <img src={LoadingCylonSvg} />
                    </Flex>
                  )}
                </div>
              </div>
              <div className={styles.spotCheckPreviewBottomSection}>
                {isAtleastTwoCameras && (
                  <KeyActions actions={shortcuts}>
                    <div className={styles.spotCheckPreviewActions}>
                      <button onClick={goToPreviousCamera} disabled={previousCameraDisabled}>
                        <MDIIcon icon={ChevronLeft} size={24} />
                        {locale.dashboard.previous}
                      </button>
                      {!isLivePreview && (
                        <Checkbox
                          name="exclude-camera"
                          labelText={locale.scheduledAIChat.excludeThisCamera}
                          onChange={toggleExcludedCamera}
                          disabled={
                            !isCurrentCameraSelected &&
                            excludedCameraIds.length + 1 >= (spotCheckEvent?.cameras.length ?? 0)
                          }
                          checked={isCurrentCameraSelected}
                        />
                      )}
                      <button onClick={goToNextCamera} disabled={nextCameraDisabled}>
                        {locale.general.next}
                        <MDIIcon icon={ChevronRight} size={24} />
                      </button>
                    </div>
                  </KeyActions>
                )}
                {!!isAtLeastTwoReportCameras && (
                  <KeyActions actions={shortcutsEventSpotCheck}>
                    <div className={styles.spotCheckPreviewActions}>
                      <button
                        onClick={handlePreviousCameraEventSpotCheck}
                        disabled={selectedCameraIndexEventSpotCheck <= 0}
                      >
                        <MDIIcon icon={ChevronLeft} size={24} />
                        {locale.dashboard.previous}
                      </button>
                      <button
                        onClick={handleNextCameraEventSpotCheck}
                        disabled={
                          !eventImagePreviewUrls?.length ||
                          selectedCameraIndexEventSpotCheck >= eventImagePreviewUrls?.length - 1
                        }
                      >
                        {locale.general.next}
                        <MDIIcon icon={ChevronRight} size={24} />
                      </button>
                    </div>
                  </KeyActions>
                )}
                {previewAnswer && (
                  <pre
                    className={styles.spotCheckPreviewAnswer}
                    data-testid={eventSpotCheckAnswer ? undefined : 'spot-check-preview-answer'}
                    dangerouslySetInnerHTML={{
                      __html: previewAnswer,
                    }}
                  />
                )}
                {!!eventSpotCheckAnswer && (
                  <FullEventSpotCheckAnswer
                    eventSpotCheckAnswer={parseEventSpotCheckAnswer(eventSpotCheckAnswer)}
                    classes={styles.spotCheckPreviewAnswer}
                    dataTestId="spot-check-preview-answer"
                    displayState
                    displayReasoning
                    displaySynopsis
                  />
                )}
                {isPolling && !previewAnswer && areCameras && (
                  <Flex justifyContent="center" gap="1rem" alignItems="center">
                    <span
                      data-testid={
                        isEventSpotCheck && sentEventSpotCheckRequest
                          ? undefined
                          : 'spot-check-loading-response'
                      }
                    >
                      {locale.scheduledAIChat.loadingResponse}
                    </span>{' '}
                    <img src={LoadingCylonSvg} />
                  </Flex>
                )}
                {isEventSpotCheck && sentEventSpotCheckRequest && (
                  <Flex justifyContent="center" gap="1rem" alignItems="center">
                    <span data-testid="spot-check-loading-response">
                      {locale.scheduledAIChat.loadingResponse}
                    </span>{' '}
                    <img src={LoadingCylonSvg} />
                  </Flex>
                )}
              </div>
            </div>

            <div className={styles.spotCheckFormViewFormWrapper}>
              <div className={styles.spotCheckFullHeight}>
                <SpotCheckForm
                  formMode={formMode}
                  spotCheck={spotCheck}
                  spotCheckForm={spotCheckForm}
                  timezoneId={timezoneId}
                  selectedReportId={[spotCheckForm.selectedEventReport?.templateId]}
                  totalEventsInLast30Days={totalEventsInLast30Days}
                  isEventsEstimateBelowLimit={isEventsEstimateBelowLimit}
                  eventsMonthlyQuotaLimit={eventsMonthlyQuotaLimit}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
